// src/pages/Tutorial.js

import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
import ko from "date-fns/locale/ko";
import SearchAddress from "@/components/Modal/Common/SearchAddress";
import Select, { components } from "react-select";
import { useFormInput, dateFormatToISOString, formatDate, onlyNumericInput, isValidDateString } from "@/utils";
import { Loading, Toast, Confirm } from "@/components";
import logoImg from "@/assets/icons/icon-mobilicar-logo_bk.png";

import { useGetMyInfoQuery } from "@/redux/memberApi";
import { useInitRentCorpInfoMutation } from "@/redux/rentCorpApi";
import { useUploadFileMutation } from "@/redux/uploadApi";
import { useGetBankListQuery } from "@/redux/metaApi";

export const Tutorial = () => {
    const navigate = useNavigate();

    const [tutorialStep, setTutorialStep] = useState(1);
    const { formData, handleChange, setFormData } = useFormInput({
        toastMsg: "",
        confirmStatus: {
            confirmPopupStatus: false,
            confirmTitle: "",
            confirmDescription: "",
            confirmButtons: [],
        },
        addressPopupStatus: false,

        // initRentCorpInfo API Body
        corpName: "",
        corpType: "CORP",
        corpNumber: "",
        bizNumber: "",
        address: "",
        addressDetail: "",
        bCode: "",
        bizStartedAt: null,
        repName: "",
        bizType: "",
        bizCategory: "",
        bankName: "",
        bankAccount: "",
        bankOwner: "",
        rentCorpUploads: [],
        bizLicFileName: "",
        bankFileName: "",
        corpSealFileName: "",
    });
    const { toastMsg, confirmStatus, addressPopupStatus } = formData;

    // API hooks
    const { data: myInfoResponse, isLoading: isMyInfoLoading, isError: isMyInfoError, error: myInfoError } = useGetMyInfoQuery();
    const [initRentCorpInfo, { isLoading: isRegLoading }] = useInitRentCorpInfoMutation();
    const [uploadFile, { isLoading: isFileUploading }] = useUploadFileMutation();

    useEffect(() => {
        let token = localStorage.getItem("access_token");
        if (!token) navigate("/login");

        console.log(myInfoResponse?.data.rentCorps[0]?.id);
        console.log(myInfoResponse?.data.member.name);
        setFormData((prev) => ({
            ...prev,
            corpName: myInfoResponse?.data.rentCorps[0]?.corpName,
            bizNumber: myInfoResponse?.data.rentCorps[0]?.bizNumber,
            repName: myInfoResponse?.data.rentCorps[0]?.repName,
            corpPhone: myInfoResponse?.data.rentCorps[0]?.corpPhone,
            bCode: myInfoResponse?.data.rentCorps[0]?.bcode,
            address: myInfoResponse?.data.rentCorps[0]?.address,
            addressDetail: myInfoResponse?.data.rentCorps[0]?.addressDetail,
        }));
    }, [navigate, myInfoResponse, isMyInfoLoading]);

    // 단계 이동 함수
    const goNextStep = () => {
        setTutorialStep((prev) => prev + 1);
    };
    const goPrevStep = () => {
        setTutorialStep((prev) => prev - 1);
    };

    const validateRegisterCorp = () => {
        // 1. 사업자명 (corpName)
        if (!formData.corpName || formData.corpName.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자명을 입력해주세요." }));
            return false;
        }
        // 2. 사업자 구분 (corpType)
        if (!formData.corpType || formData.corpType.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자 구분을 선택해주세요." }));
            return false;
        }
        // 3. 법인 등록번호 (bizNumber)
        if (!formData.bizNumber || formData.bizNumber.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자 등록번호를 입력해주세요." }));
            return false;
        }
        // 4. 사업자 등록번호 (corpNumber)
        if (formData.corpType === "CORP") {
            if (!formData.corpNumber || formData.corpNumber.trim() === "") {
                setFormData((prev) => ({ ...prev, toastMsg: "법인 등록번호를 입력해주세요." }));
                return false;
            }
        }
        // 5. 사업자 주소 (address)
        if (!formData.address || formData.address.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자 주소를 입력해주세요." }));
            return false;
        }
        // 6. 사업자 상세주소 (addressDetail)
        if (!formData.addressDetail || formData.addressDetail.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "사업자 상세주소를 입력해주세요." }));
            return false;
        }
        // 8. 개업연월 (bizStartedAt)
        if (!formData.bizStartedAt) {
            setFormData((prev) => ({ ...prev, toastMsg: "개업연월을 입력해주세요." }));
            return false;
        }

        if (formData.bizStartedAt && !isValidDateString(formData.bizStartedAt)) {
            setFormData((prev) => ({ ...prev, toastMsg: "개업연월이 올바르지 않습니다." }));
            return false;
        }

        // 9. 대표자명 (repName)
        if (!formData.repName || formData.repName.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "대표자명을 입력해주세요." }));
            return false;
        }
        // 10. 업태 (bizType)
        if (!formData.bizType || formData.bizType.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "업태를 입력해주세요." }));
            return false;
        }
        // 11. 종목 (bizCategory)
        if (!formData.bizCategory || formData.bizCategory.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "종목을 입력해주세요." }));
            return false;
        }
        // 12. 은행 (bankName)
        if (!formData.bankName || formData.bankName.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "은행명을 입력해주세요." }));
            return false;
        }
        // 13. 계좌번호 (bankAccount)
        if (!formData.bankAccount || formData.bankAccount.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "계좌번호를 입력해주세요." }));
            return false;
        }
        // 14. 계좌 소유주 (bankOwner)
        if (!formData.bankOwner || formData.bankOwner.trim() === "") {
            setFormData((prev) => ({ ...prev, toastMsg: "예금주를 입력해주세요." }));
            return false;
        }
        // 15. 첨부파일 (rentCorpUploads) - 배열 존재 여부 및 필수 업로드 타입 확인
        if (!formData.rentCorpUploads || !Array.isArray(formData.rentCorpUploads)) {
            setFormData((prev) => ({ ...prev, toastMsg: "첨부파일을 추가해주세요." }));
            return false;
        }

        // 필수 첨부파일 타입: BIZ_LIC, BANK, CORP_SEAL
        const requiredUploadTypes = ["BIZ_LIC", "BANK" /* "CORP_SEAL" */];
        // 각 타입에 대한 한글 설명 매핑
        const typeNames = {
            BIZ_LIC: "사업자 등록증 파일",
            BANK: "통장 사본 파일",
            // CORP_SEAL: "법인 인감 파일",
        };

        for (const type of requiredUploadTypes) {
            const exists = formData.rentCorpUploads.some((upload) => upload.uploadType === type);
            if (!exists) {
                setFormData((prev) => ({
                    ...prev,
                    toastMsg: `첨부파일에 ${typeNames[type]}이 필요합니다.`,
                }));
                return false;
            }
        }
        return true;
    };

    // 실제 등록 로직 분리
    const doRegisterCorp = async () => {
        try {
            // Request Body
            const body = {
                corpName: formData.corpName, // 사업자명
                corpType: formData.corpType, // 사업자 구분 "CORP"/"PERSONAL"
                corpNumber: formData.corpNumber, // 법인 등록번호
                bizNumber: formData.bizNumber, // 사업자 등록번호
                address: formData.address, // 사업자 주소
                addressDetail: formData.addressDetail, // 사업자 상세주소
                corpPhone: formData.corpPhone, // 사업자 전화번호
                bCode: formData.bCode, // 사업자 주소 코드
                bizStartedAt: formData.bizStartedAt
                    ? typeof formData.bizStartedAt === "string"
                        ? formData.bizStartedAt
                        : dateFormatToISOString(formData.bizStartedAt, "start")
                    : "", // 개업연월
                repName: formData.repName, // 대표자명
                bizType: formData.bizType, // 업태
                bizCategory: formData.bizCategory, // 종목
                bankName: formData.bankName, // 은행
                bankAccount: formData.bankAccount, // 계좌번호
                bankOwner: formData.bankOwner, // 계좌 소유주
                rentCorpUploads: formData.rentCorpUploads, // 첨부파일 (BIZ_LIC 필수값)
            };

            const res = await initRentCorpInfo({ rentCorpId: myInfoResponse?.data.rentCorps[0]?.id, body }).unwrap();
            console.log("사업자 등록 완료:", res);
            setFormData((prev) => ({
                ...prev,
                toastMsg: "사업자 등록이 완료되었습니다.",
            }));
            setTutorialStep(4);
        } catch (err) {
            console.error(err);
            setFormData((prev) => ({
                ...prev,
                toastMsg: err?.message || "사업자 등록 중 오류 발생",
            }));
        }
    };

    // 최종 "사업자 등록하기" 클릭
    const handleRegisterCorp = async () => {
        if (!validateRegisterCorp()) return;

        // 인감 도장 파일 미등록 시 Confirm
        const hasCorpSeal = formData.rentCorpUploads?.some((upload) => upload.uploadType === "CORP_SEAL");
        if (!hasCorpSeal) {
            setFormData((prev) => ({
                ...prev,
                confirmStatus: {
                    confirmPopupStatus: true,
                    confirmTitle: "인감 도장 이미지가 등록되지 않았습니다.",
                    confirmDescription: "미등록 상태로 계약서를 발송 하실경우 미등록으로 인한 법적인 책임은 모빌리카와 무관합니다.",
                    confirmButtons: [
                        {
                            label: "확인",
                            className: "btn-primary",
                            onClick: async () => {
                                setFormData((prev2) => ({
                                    ...prev2,
                                    confirmStatus: { confirmPopupStatus: false },
                                }));
                                await doRegisterCorp();
                            },
                        },
                        {
                            label: "취소",
                            className: "btn-cancel",
                            onClick: () =>
                                setFormData((prev2) => ({
                                    ...prev2,
                                    confirmStatus: { confirmPopupStatus: false },
                                })),
                        },
                    ],
                },
            }));
            return;
        }
        await doRegisterCorp();
    };

    return (
        <>
            {toastMsg && <Toast visible={true} msg={toastMsg} setFormData={setFormData} />}

            <div className="app">
                <div className="signup-container">
                    <div className="signup-banner">
                        <span>
                            <img src={logoImg} alt="Logo" />
                        </span>
                        <span>MOBILICAR PARTNERS</span>
                    </div>

                    {/* 단계별 표시 */}
                    {tutorialStep === 1 && <TutorialA myInfoResponse={myInfoResponse} goNextStep={goNextStep} />}
                    {tutorialStep === 2 && <TutorialB myInfoResponse={myInfoResponse} goNextStep={goNextStep} goPrevStep={goPrevStep} />}
                    {tutorialStep === 3 && (
                        <TutorialC
                            myInfoResponse={myInfoResponse}
                            formData={formData}
                            handleChange={handleChange}
                            setFormData={setFormData}
                            goPrevStep={goPrevStep}
                            handleRegisterCorp={handleRegisterCorp}
                            uploadFile={uploadFile}
                        />
                    )}
                    {tutorialStep === 4 && <TutorialD navigate={navigate} />}
                </div>
            </div>

            {/* Confirm 팝업 */}
            {confirmStatus?.confirmPopupStatus && (
                <Confirm
                    layer={true}
                    onClose={() =>
                        setFormData((prev) => ({
                            ...prev,
                            confirmStatus: { confirmPopupStatus: false },
                        }))
                    }
                    title={confirmStatus.confirmTitle}
                    description={confirmStatus.confirmDescription}
                    buttons={confirmStatus.confirmButtons}
                />
            )}

            {/* 주소 검색 팝업 */}
            {addressPopupStatus && (
                <SearchAddress
                    modalStatus={addressPopupStatus}
                    setModalStatus={() =>
                        setFormData((prev) => ({
                            ...prev,
                            addressPopupStatus: false,
                        }))
                    }
                    setAddress={(data) =>
                        setFormData((prev) => ({
                            ...prev,
                            address: data.address,
                            bCode: data.bcode,
                        }))
                    }
                />
            )}
        </>
    );
};

// ---------------------------------------------------------------------
// TutorialA
// ---------------------------------------------------------------------
const TutorialA = ({ myInfoResponse, goNextStep }) => {
    return (
        <div className="signup-content">
            <div className="app-page" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div className="page-header">
                    <span className="header-tit">
                        <em>{myInfoResponse?.data.member.name}님 환영합니다.</em>
                    </span>
                    <span className="form-sub_title">스마트한 렌터카 업무, 모빌리카로 시작해볼까요?</span>
                </div>
                <div className="page-content">
                    <button type="button" className="btn-login" onClick={goNextStep}>
                        시작하기
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------
// TutorialB
// ---------------------------------------------------------------------
const TutorialB = ({ myInfoResponse, goNextStep, goPrevStep }) => {
    return (
        <div className="signup-content">
            <div className="app-page" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div className="page-header">
                    <span className="header-tit">
                        <em>
                            카피드 시작을 <br />
                            위한 첫 걸음!
                        </em>
                    </span>
                    <span className="form-sub_title">
                        간단한 설정만 하면 카피드 사용을 위한 <br />
                        모든 준비가 완료됩니다.
                    </span>
                </div>
                <div className="page-content">
                    <button type="button" className="btn-login" onClick={goNextStep}>
                        사업자 등록하기
                    </button>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------
// TutorialC
// ---------------------------------------------------------------------
const TutorialC = ({ myInfoResponse, formData, handleChange, setFormData, goPrevStep, handleRegisterCorp, uploadFile }) => {
    useEffect(() => {
        console.log(formData);
    }, [formData]);

    return (
        <div className="signup-content">
            <div className="app-page">
                <div className="page-header">
                    <span className="header-tit">
                        스마트한 렌터카 업무, <br />
                        카피드로 시작해볼까요?
                    </span>
                    <span className="form-sub_title"></span>
                </div>
                <div className="page-content">
                    {/* (1) 사업자 정보 */}
                    <div className="auth-container">
                        <div className="section-head">
                            <span className="form-item_tit">사업자 정보</span>
                            <div className="form-item_etc"></div>
                        </div>
                        <div className="section-body">
                            <div className="form-wrapper">
                                <VehicleInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                            </div>
                        </div>
                    </div>

                    {/* (2) 은행/계좌 정보 */}
                    <div className="auth-container">
                        <div className="section-head">
                            <span className="form-item_tit">은행 정보</span>
                            <div className="form-item_etc"></div>
                        </div>
                        <div className="section-body">
                            <div className="form-wrapper">
                                <InsuranceInfo formData={formData} handleChange={handleChange} setFormData={setFormData} />
                            </div>
                        </div>
                    </div>

                    {/* (3) 첨부파일 등록 */}
                    <div className="auth-container">
                        <div className="section-head">
                            <span className="form-item_tit">첨부파일 등록</span>
                        </div>
                        <div className="section-body">
                            <div className="form-wrapper">
                                <ResultImage
                                    formData={formData}
                                    handleChange={handleChange}
                                    setFormData={setFormData}
                                    uploadFile={uploadFile}
                                />
                            </div>
                        </div>
                    </div>

                    {/* 버튼: 이전/사업자 등록하기 */}
                    <div className="auth-container">
                        <div className="form-button">
                            <div className="button-wrapper" style={{ display: "flex", gap: "10px" }}>
                                <button className="btn btn-md btn-primary" onClick={handleRegisterCorp}>
                                    사업자 등록하기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

// ---------------------------------------------------------------------
// VehicleInfo => 사업자 정보 입력
// (TutorialC 상단)
// ---------------------------------------------------------------------
const VehicleInfo = ({ formData, handleChange, setFormData }) => {
    const {
        corpName,
        corpType,
        corpNumber,
        bizNumber,
        repName,
        corpPhone,
        bizType,
        bizCategory,
        bizStartedAt,
        address,
        addressDetail,
        bCode,
    } = formData;

    // 예시 Select
    const corpTypeOptions = [
        { value: "CORP", label: "법인" },
        { value: "PERSONAL", label: "개인" },
    ];

    return (
        <>
            {/* 사업자명 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">사업자명</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="corpName" placeholder="사업자명" value={corpName} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>

            {/* 사업자 구분 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">사업자 구분</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <Select
                                options={corpTypeOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                placeholder="사업자 구분"
                                value={corpTypeOptions.find((opt) => opt.value === corpType)}
                                onChange={(option) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        corpType: option.value,
                                    }))
                                }
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 법인 등록번호 */}
            {formData.corpType === "CORP" && (
                <div className="form-item">
                    <div className="item-label">
                        <span className="label-title">법인 등록번호</span>
                    </div>
                    <div className="item-content">
                        <div className="content-field">
                            <div className="field-row">
                                <input
                                    type="text"
                                    name="corpNumber"
                                    placeholder="-없이 숫자만 입력"
                                    value={corpNumber}
                                    onChange={handleChange}
                                    onInput={onlyNumericInput}
                                />
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* 사업자 등록번호 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">사업자 등록번호</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input
                                type="text"
                                name="bizNumber"
                                placeholder="-없이 숫자만 입력"
                                value={bizNumber}
                                onChange={handleChange}
                                onInput={onlyNumericInput}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 대표자명 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">대표자명</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="repName" placeholder="대표자명" value={repName} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>

            {/* 사업자 전화번호 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">사업자 전화번호</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input
                                type="text"
                                name="corpPhone"
                                placeholder="-없이 숫자만 입력"
                                value={corpPhone}
                                onChange={handleChange}
                                maxLength={11}
                                onInput={onlyNumericInput}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 업태 / 종목 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">업태</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="bizType" placeholder="ex) 서비스" value={bizType} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">종목</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="bizCategory" placeholder="ex) 렌터카" value={bizCategory} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>

            {/* 개업연월일 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">개업연월일</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            {/* <DatePicker
                                locale={ko}
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={20}
                                selected={bizStartedAt}
                                onChange={(date) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        bizStartedAt: date,
                                    }))
                                }
                                dateFormat="yyyy.MM.dd"
                                onKeyDown={(e) => e.preventDefault()}
                                placeholderText="YYYY.MM.DD"
                            /> */}
                            <input
                                type="text"
                                name="bizStartedAt"
                                value={formatDate(bizStartedAt)}
                                placeholder="ex) 20250101"
                                onChange={handleChange}
                                maxLength={10}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* 사업장 주소 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">사업장 주소</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            {/* 법정동코드 */}
                            <input
                                type="text"
                                name="bCode"
                                placeholder="bCode"
                                value={bCode}
                                onChange={handleChange}
                                readOnly
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        addressPopupStatus: true,
                                    }))
                                }
                                style={{ display: "none" }}
                            />
                            {/* 주소 */}
                            <input
                                type="text"
                                name="address"
                                placeholder="주소"
                                value={address}
                                onChange={handleChange}
                                readOnly
                                onClick={() =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        addressPopupStatus: true,
                                    }))
                                }
                            />
                        </div>
                        <button
                            type="button"
                            className="btn btn-xs btn-primary"
                            onClick={() => {
                                setFormData((prevState) => ({
                                    ...prevState,
                                    addressPopupStatus: true,
                                }));
                            }}
                        >
                            주소검색
                        </button>
                    </div>

                    {/* 상세주소 */}
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="addressDetail" placeholder="상세주소" value={addressDetail} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// ---------------------------------------------------------------------
// InsuranceInfo
// ---------------------------------------------------------------------
const InsuranceInfo = ({ formData, handleChange, setFormData }) => {
    const { bankName, bankAccount, bankOwner } = formData;

    // API 호출: 은행리스트 조회
    const { data: bankData, isLoading: bankLoading } = useGetBankListQuery();

    // 은행 Select 예시
    const bankOptions = bankData?.data
        ? bankData.data.map((bank) => ({
              value: bank.code,
              label: bank.name,
              icon: bank.imgUrl,
          }))
        : [];

    // 커스텀 옵션: 드롭다운에서는 이미지와 라벨 모두 표시
    const IconOption = (props) => (
        <components.Option {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} />
            {props.data.label}
        </components.Option>
    );

    // 커스텀 SingleValue: 선택된 값은 라벨만 표시
    const CustomSingleValue = (props) => (
        <components.SingleValue {...props}>
            <img src={props.data.icon} alt={props.data.label} width={20} height={20} /> {props.data.label}
        </components.SingleValue>
    );

    return (
        <>
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">은행</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <Select
                                options={bankOptions}
                                className="custom-select"
                                classNamePrefix="custom-select"
                                placeholder="은행 선택"
                                value={bankOptions.find((opt) => opt.value === bankName)}
                                onChange={(option) =>
                                    setFormData((prev) => ({
                                        ...prev,
                                        bankName: option.value,
                                    }))
                                }
                                components={{ Option: IconOption, SingleValue: CustomSingleValue }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">계좌번호</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="bankAccount" placeholder="계좌번호" value={bankAccount} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">예금주</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" name="bankOwner" placeholder="예금주" value={bankOwner} onChange={handleChange} />
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

// ---------------------------------------------------------------------
// ResultImage => 첨부파일(사업자등록증 등)
// ---------------------------------------------------------------------
const ResultImage = ({ formData, setFormData, uploadFile }) => {
    // 화면 표시용 파일명
    const { bizLicFileName, bankFileName, corpSealFileName } = formData;

    // 숨겨진 input ref
    const bizLicRef = useRef(null);
    const bankRef = useRef(null);
    const corpSealRef = useRef(null);

    // 파일첨부 버튼 → ref.click()
    const handleButtonClick = (type) => {
        if (type === "BIZ_LIC") {
            bizLicRef.current?.click();
        } else if (type === "BANK") {
            bankRef.current?.click();
        } else if (type === "CORP_SEAL") {
            corpSealRef.current?.click();
        }
    };

    // 업로드 처리
    const handleFileChange = async (e, uploadType) => {
        const fileObj = e.target.files[0];
        if (!fileObj) return;

        setFormData((prev) => ({
            ...prev,
            isLoading: true,
        }));

        try {
            const formDataObj = new FormData();
            formDataObj.append("file", fileObj);

            const res = await uploadFile(formDataObj).unwrap();
            const newUploadId = res.data?.id || 0;

            setFormData((prev) => {
                // rentCorpUploads 업데이트
                const updated = [...(prev.rentCorpUploads || [])];
                const idx = updated.findIndex((x) => x.uploadType === uploadType);
                if (idx >= 0) {
                    updated[idx] = { ...updated[idx], uploadId: newUploadId };
                } else {
                    updated.push({ uploadId: newUploadId, uploadType });
                }
                // 화면 표시용 파일명
                let newState = {};
                if (uploadType === "BIZ_LIC") {
                    newState = { bizLicFileName: fileObj.name };
                } else if (uploadType === "BANK") {
                    newState = { bankFileName: fileObj.name };
                } else if (uploadType === "CORP_SEAL") {
                    newState = { corpSealFileName: fileObj.name };
                }
                return {
                    ...prev,
                    rentCorpUploads: updated,
                    ...newState,
                };
            });
        } catch (error) {
            console.error(error);
            setFormData((prev) => ({
                ...prev,
                toastMsg: error?.data?.message || "파일 업로드 실패",
            }));
        } finally {
            setFormData((prev) => ({
                ...prev,
                isLoading: false,
            }));
        }
    };

    return (
        <>
            {/* 사업자등록증 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">사업자 등록증</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" placeholder="4MB 이하 PDF/JPG/PNG 파일" readOnly value={bizLicFileName || ""} />
                        </div>
                        <button type="button" className="btn btn-xs btn-primary" onClick={() => handleButtonClick("BIZ_LIC")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={bizLicRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "BIZ_LIC")}
                        />
                    </div>
                </div>
            </div>

            {/* 통장 사본 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">통장 사본</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input type="text" placeholder="4MB 이하 PDF/JPG/PNG 파일" readOnly value={bankFileName || ""} />
                        </div>
                        <button type="button" className="btn btn-xs btn-primary" onClick={() => handleButtonClick("BANK")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={bankRef}
                            style={{ display: "none" }}
                            accept=".pdf,.jpg,.png,.jpeg"
                            onChange={(e) => handleFileChange(e, "BANK")}
                        />
                    </div>
                </div>
            </div>

            {/* 법인 인감 */}
            <div className="form-item">
                <div className="item-label">
                    <span className="label-title">법인 인감</span>
                </div>
                <div className="item-content">
                    <div className="content-field">
                        <div className="field-row">
                            <input
                                type="text"
                                placeholder="흰색 배경에 날인 / 500KB 이하 JPG/PNG"
                                readOnly
                                value={corpSealFileName || ""}
                            />
                        </div>
                        <button type="button" className="btn btn-xs btn-primary" onClick={() => handleButtonClick("CORP_SEAL")}>
                            파일첨부
                        </button>
                        <input
                            type="file"
                            ref={corpSealRef}
                            style={{ display: "none" }}
                            accept=".jpg,.png"
                            onChange={(e) => handleFileChange(e, "CORP_SEAL")}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};

// ---------------------------------------------------------------------
// TutorialD
// ---------------------------------------------------------------------
const TutorialD = ({ navigate }) => {
    return (
        <div className="signup-content">
            <div className="app-page" style={{ display: "flex", flexDirection: "column", justifyContent: "center" }}>
                <div className="page-header">
                    <span className="header-tit">
                        <em>
                            사업자 등록이 <br />
                            완료되었습니다.
                        </em>
                    </span>
                    <span className="form-sub_title">스마트한 렌터카 업무, 모빌리카로 시작해볼까요?</span>
                </div>
                <div className="page-content">
                    <button type="button" className="btn-login" onClick={() => (window.location.href = "/")}>
                        메인으로
                    </button>
                </div>
            </div>
        </div>
    );
};
